import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Edit3, 
  MessageCircle, 
  Clock, 
  User, 
  Trash2, 
  Send,
  ChevronDown,
  ChevronRight,
  Calendar,
  Flag,
  Tag,
  CheckCircle2,
  Circle,
  AlertCircle,
  Play,
  Pause,
  RotateCcw,
  Archive,
  UserPlus,
  MapPin,
  FileText,
  Hash,
  Target,
  Zap,
  Settings,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  ArrowRight,
  Users,
  Building,
  Briefcase,
  Star,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Activity,
  Upload,
  Download,
  Paperclip,
  File,
  Image
} from 'lucide-react';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import ControlIdPicker from './ControlIdPicker';
import toast from 'react-hot-toast';

/**
 * Professional Task Detail Panel - Enhanced Jira-style interface
 */
const TaskDetailPanelPro = ({ 
  task, 
  isOpen, 
  onClose, 
  onTaskUpdated,
  className = "" 
}) => {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Form data state - mapped to actual database fields
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    control_id: '',
    question_id: '',
    assignee_id: '',
    priority: 'medium',
    status: 'not_started',
    task_type: 'general',
    due_date: '',
    estimated_hours: '',
    actual_hours: '',
    tags: [],
    metadata: {}
  });
  
  // UI state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [commentsPanelHeight, setCommentsPanelHeight] = useState(400);
  const [isResizingComments, setIsResizingComments] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showWatchers, setShowWatchers] = useState(false);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Refs
  const commentsEndRef = useRef(null);
  const newCommentRef = useRef(null);
  const resizeRef = useRef(null);
  const panelRef = useRef(null);

  // Initialize form data when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        control_id: task.control_id || '',
        question_id: task.question_id || '',
        assignee_id: task.assignee_id || '',
        priority: task.priority || 'medium',
        status: task.status || 'not_started',
        task_type: task.task_type || 'general',
        due_date: task.due_date ? task.due_date.split('T')[0] : '',
        estimated_hours: task.estimated_hours || '',
        actual_hours: task.actual_hours || '',
        tags: task.tags || [],
        metadata: task.metadata || {}
      });
      setEditMode(false);
      setErrors({});
    }
  }, [task]);

  // Load comments and attachments when task changes
  useEffect(() => {
    if (task && isOpen) {
      loadComments();
      loadAttachments();
    }
  }, [task, isOpen]);

  // Comments resizing functionality
  const handleCommentsResize = useCallback((e) => {
    if (!isResizingComments) return;
    
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      const newHeight = rect.bottom - e.clientY;
      const minHeight = 200;
      const maxHeight = 600;
      setCommentsPanelHeight(Math.max(minHeight, Math.min(maxHeight, newHeight)));
    }
  }, [isResizingComments]);

  const handleCommentsResizeStart = () => {
    setIsResizingComments(true);
  };

  const handleCommentsResizeEnd = useCallback(() => {
    setIsResizingComments(false);
  }, []);

  useEffect(() => {
    if (isResizingComments) {
      document.addEventListener('mousemove', handleCommentsResize);
      document.addEventListener('mouseup', handleCommentsResizeEnd);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleCommentsResize);
        document.removeEventListener('mouseup', handleCommentsResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizingComments, handleCommentsResize, handleCommentsResizeEnd]);

  // Load comments
  const loadComments = async () => {
    if (!task) return;
    
    try {
      setCommentsLoading(true);
      const response = await taskService.getTaskComments(task.id);
      setComments(response.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  // Add comment
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !task) return;
    
    try {
      setAddingComment(true);
      const response = await taskService.addTaskComment(task.id, newComment.trim());
      
      setComments(prev => [...prev, response.comment]);
      setNewComment('');
      toast.success('Comment added');
      
      if (newCommentRef.current) {
        newCommentRef.current.focus();
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setDeletingCommentId(commentId);
      await taskService.deleteTaskComment(commentId);
      
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  // Load attachments
  const loadAttachments = async () => {
    if (!task) return;
    
    try {
      setAttachmentsLoading(true);
      const response = await taskService.getTaskAttachments(task.id);
      setAttachments(response.attachments || []);
    } catch (error) {
      console.error('Failed to load attachments:', error);
      toast.error('Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!task || files.length === 0) return;

    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      try {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`);
          return null;
        }

        const response = await taskService.uploadTaskAttachment(task.id, file);
        return response.attachment;
      } catch (error) {
        console.error('Failed to upload file:', error);
        toast.error(`Failed to upload "${file.name}": ${error.message}`);
        return null;
      }
    });

    try {
      const uploadedAttachments = await Promise.all(uploadPromises);
      const successfulUploads = uploadedAttachments.filter(Boolean);
      
      if (successfulUploads.length > 0) {
        setAttachments(prev => [...prev, ...successfulUploads]);
        toast.success(`Successfully uploaded ${successfulUploads.length} file(s)`);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileUpload(files);
    }
    // Clear the input so the same file can be uploaded again
    e.target.value = '';
  };

  // Handle delete attachment
  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (!confirm(`Are you sure you want to delete "${fileName}"?`)) {
      return;
    }

    try {
      setDeletingAttachmentId(attachmentId);
      await taskService.deleteTaskAttachment(attachmentId);
      
      setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error('Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await taskService.downloadTaskAttachment(attachmentId);
      
      // Create download link
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [task]);

  // Get file icon based on file type
  const getFileIcon = (fileType, fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (fileType.startsWith('image/')) {
      return <Image className="h-5 w-5 text-green-600" />;
    } else if (fileType.startsWith('video/')) {
      return <File className="h-5 w-5 text-purple-600" />;
    } else if (fileType.startsWith('audio/')) {
      return <File className="h-5 w-5 text-blue-600" />;
    } else if (fileType.includes('pdf') || extension === 'pdf') {
      return <FileText className="h-5 w-5 text-red-600" />;
    } else if (fileType.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(extension)) {
      return <FileText className="h-5 w-5 text-emerald-600" />;
    } else if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'html', 'css', 'json', 'xml'].includes(extension)) {
      return <FileText className="h-5 w-5 text-indigo-600" />;
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else {
      return <File className="h-5 w-5 text-gray-600" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.control_id.trim()) {
      newErrors.control_id = 'Control ID is required';
    } else if (!/^ML[1-3]-[A-Z]{2,3}-\d{2}$/.test(formData.control_id)) {
      newErrors.control_id = 'Invalid control ID format (e.g., ML1-PA-01)';
    }
    
    if (formData.estimated_hours && (isNaN(parseFloat(formData.estimated_hours)) || parseFloat(formData.estimated_hours) < 0)) {
      newErrors.estimated_hours = 'Must be a valid positive number';
    }
    
    if (formData.actual_hours && (isNaN(parseFloat(formData.actual_hours)) || parseFloat(formData.actual_hours) < 0)) {
      newErrors.actual_hours = 'Must be a valid positive number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const updateData = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        actual_hours: formData.actual_hours ? parseFloat(formData.actual_hours) : null,
        due_date: formData.due_date || null,
        assignee_id: formData.assignee_id || null,
        question_id: formData.question_id || null
      };

      const response = await taskService.updateTask(task.id, updateData);
      onTaskUpdated(response.task);
      setEditMode(false);
      toast.success('Task updated successfully');
      
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error(`Failed to update task: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Quick status update
  const handleStatusChange = async (newStatus) => {
    try {
      const response = await taskService.updateTaskStatus(task.id, newStatus);
      onTaskUpdated(response.task);
      toast.success('Status updated');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Priority color mapping
  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'critical': 
        return { 
          color: 'text-red-700 bg-red-50 border-red-200', 
          icon: AlertCircle,
          label: 'Critical',
          badgeColor: 'bg-red-500'
        };
      case 'high': 
        return { 
          color: 'text-orange-700 bg-orange-50 border-orange-200', 
          icon: Flag,
          label: 'High',
          badgeColor: 'bg-orange-500'
        };
      case 'medium': 
        return { 
          color: 'text-yellow-700 bg-yellow-50 border-yellow-200', 
          icon: Flag,
          label: 'Medium',
          badgeColor: 'bg-yellow-500'
        };
      case 'low': 
        return { 
          color: 'text-green-700 bg-green-50 border-green-200', 
          icon: Flag,
          label: 'Low',
          badgeColor: 'bg-green-500'
        };
      default: 
        return { 
          color: 'text-gray-700 bg-gray-50 border-gray-200', 
          icon: Flag,
          label: 'None',
          badgeColor: 'bg-gray-400'
        };
    }
  };

  // Status configuration
  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { 
          color: 'text-green-700 bg-green-50 border-green-200', 
          icon: CheckCircle2, 
          label: 'Done',
          actionColor: 'bg-green-600 hover:bg-green-700'
        };
      case 'in_progress':
        return { 
          color: 'text-blue-700 bg-blue-50 border-blue-200', 
          icon: Play, 
          label: 'In Progress',
          actionColor: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'blocked':
        return { 
          color: 'text-red-700 bg-red-50 border-red-200', 
          icon: AlertCircle, 
          label: 'Blocked',
          actionColor: 'bg-red-600 hover:bg-red-700'
        };
      case 'under_review':
        return { 
          color: 'text-purple-700 bg-purple-50 border-purple-200', 
          icon: Eye, 
          label: 'In Review',
          actionColor: 'bg-purple-600 hover:bg-purple-700'
        };
      case 'cancelled':
        return { 
          color: 'text-gray-700 bg-gray-50 border-gray-200', 
          icon: Archive, 
          label: 'Cancelled',
          actionColor: 'bg-gray-600 hover:bg-gray-700'
        };
      default:
        return { 
          color: 'text-slate-700 bg-slate-50 border-slate-200', 
          icon: Circle, 
          label: 'To Do',
          actionColor: 'bg-slate-600 hover:bg-slate-700'
        };
    }
  };

  // Get action buttons based on current status
  const getActionButtons = () => {
    const actions = [];
    
    if (task.status === 'not_started') {
      actions.push({
        label: 'Start Task',
        icon: Play,
        onClick: () => handleStatusChange('in_progress'),
        className: 'bg-blue-600 hover:bg-blue-700 text-white'
      });
    }
    
    if (task.status === 'in_progress') {
      actions.push({
        label: 'Mark Complete',
        icon: CheckCircle2,
        onClick: () => handleStatusChange('completed'),
        className: 'bg-green-600 hover:bg-green-700 text-white'
      });
      actions.push({
        label: 'Submit for Review',
        icon: Eye,
        onClick: () => handleStatusChange('under_review'),
        className: 'bg-purple-600 hover:bg-purple-700 text-white'
      });
    }
    
    if (task.status === 'under_review') {
      actions.push({
        label: 'Approve & Complete',
        icon: CheckCircle2,
        onClick: () => handleStatusChange('completed'),
        className: 'bg-green-600 hover:bg-green-700 text-white'
      });
      actions.push({
        label: 'Send Back',
        icon: RotateCcw,
        onClick: () => handleStatusChange('in_progress'),
        className: 'bg-orange-600 hover:bg-orange-700 text-white'
      });
    }
    
    if (task.status !== 'completed' && task.status !== 'cancelled') {
      actions.push({
        label: 'Block Task',
        icon: AlertCircle,
        onClick: () => handleStatusChange('blocked'),
        className: 'bg-red-600 hover:bg-red-700 text-white'
      });
    }

    return actions;
  };

  if (!isOpen || !task) return null;

  const priorityConfig = getPriorityConfig(task.priority);
  const statusConfig = getStatusConfig(task.status);
  const PriorityIcon = priorityConfig.icon;
  const StatusIcon = statusConfig.icon;
  const actionButtons = getActionButtons();

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isOpen ? 'bg-opacity-25' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Enhanced Slide-out Panel */}
      <div 
        ref={panelRef}
        className={`fixed top-0 right-0 h-full bg-white shadow-2xl transform transition-all duration-300 ease-in-out z-50 border-l border-gray-200 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isMaximized ? 'w-full' : 'w-full max-w-5xl'} ${className}`}
      >
        <div className="flex flex-col h-full">
          {/* Enhanced Header */}
          <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg border ${statusConfig.color}`}>
                    <StatusIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 truncate max-w-md">
                      {task.title}
                    </h2>
                    <div className="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                      <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs font-medium">
                        {task.control_id}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${priorityConfig.color}`}>
                        <PriorityIcon className="h-3 w-3 mr-1" />
                        {priorityConfig.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={isMaximized ? "Restore" : "Maximize"}
                >
                  {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Action Buttons - Jira Style */}
            <div className="flex items-center space-x-2 mt-4">
              {actionButtons.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-transparent transition-colors duration-200 ${action.className}`}
                >
                  <action.icon className="h-4 w-4 mr-2" />
                  {action.label}
                </button>
              ))}
              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit
                </button>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-6 mt-4 border-b border-gray-200">
              {[
                { id: 'details', label: 'Details', icon: FileText },
                { id: 'activity', label: `Activity (${comments.length})`, icon: Activity },
                { id: 'attachments', label: `Attachments (${attachments.length})`, icon: Paperclip }
              ].map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center">
                      <TabIcon className="h-4 w-4 mr-2" />
                      {tab.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'details' && (
              <div className="h-full overflow-y-auto">
                {editMode ? (
                  /* Edit Form */
                  <form onSubmit={handleSubmit} className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column */}
                      <div className="space-y-6">
                        {/* Title */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.title ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter task title"
                          />
                          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            rows={5}
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            placeholder="Describe what needs to be done..."
                          />
                        </div>

                        {/* Control ID - Now Editable */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Control ID *
                          </label>
                          <ControlIdPicker
                            value={formData.control_id}
                            onChange={(value) => handleChange('control_id', value)}
                            error={errors.control_id}
                            placeholder="Select or search for a control ID..."
                          />
                          {errors.control_id && <p className="mt-1 text-sm text-red-600">{errors.control_id}</p>}
                        </div>

                        {/* Question ID */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Question ID
                          </label>
                          <input
                            type="text"
                            value={formData.question_id}
                            onChange={(e) => handleChange('question_id', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Optional question ID"
                          />
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-6">
                        {/* Status & Priority */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                              value={formData.status}
                              onChange={(e) => handleChange('status', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="not_started">To Do</option>
                              <option value="in_progress">In Progress</option>
                              <option value="blocked">Blocked</option>
                              <option value="under_review">In Review</option>
                              <option value="completed">Done</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                            <select
                              value={formData.priority}
                              onChange={(e) => handleChange('priority', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          </div>
                        </div>

                        {/* Task Type */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
                          <select
                            value={formData.task_type}
                            onChange={(e) => handleChange('task_type', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="general">General</option>
                            <option value="remediation">Remediation</option>
                            <option value="assessment">Assessment</option>
                            <option value="review">Review</option>
                            <option value="implementation">Implementation</option>
                            <option value="documentation">Documentation</option>
                            <option value="testing">Testing</option>
                          </select>
                        </div>

                        {/* Due Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                          <input
                            type="date"
                            value={formData.due_date}
                            onChange={(e) => handleChange('due_date', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Time Estimates */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Hours</label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={formData.estimated_hours}
                              onChange={(e) => handleChange('estimated_hours', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.estimated_hours ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="0.0"
                            />
                            {errors.estimated_hours && <p className="mt-1 text-sm text-red-600">{errors.estimated_hours}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Actual Hours</label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={formData.actual_hours}
                              onChange={(e) => handleChange('actual_hours', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.actual_hours ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="0.0"
                            />
                            {errors.actual_hours && <p className="mt-1 text-sm text-red-600">{errors.actual_hours}</p>}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => setEditMode(false)}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                ) : (
                  /* View Mode */
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column - Task Details */}
                      <div className="space-y-6">
                        {/* Description */}
                        {task.description && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                              <FileText className="h-4 w-4 mr-2" />
                              Description
                            </h3>
                            <div className="bg-gray-50 rounded-lg p-4">
                              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {task.description}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Tags */}
                        {task.tags && task.tags.length > 0 && (
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                              <Tag className="h-4 w-4 mr-2" />
                              Tags
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {task.tags.map((tag, index) => (
                                <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Column - Metadata */}
                      <div className="space-y-6">
                        {/* Status & Priority */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                            <Target className="h-4 w-4 mr-2" />
                            Status & Priority
                          </h3>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Status</span>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}>
                                <StatusIcon className="h-4 w-4 mr-1" />
                                {statusConfig.label}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Priority</span>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${priorityConfig.color}`}>
                                <div className={`w-2 h-2 rounded-full mr-2 ${priorityConfig.badgeColor}`}></div>
                                {priorityConfig.label}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Task Information */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                            <Briefcase className="h-4 w-4 mr-2" />
                            Task Information
                          </h3>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Type</span>
                              <span className="text-sm font-medium capitalize">
                                {task.task_type}
                              </span>
                            </div>
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Control</span>
                              <span className="text-sm font-mono font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {task.control_id}
                              </span>
                            </div>
                            {task.question_id && (
                              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Question</span>
                                <span className="text-sm font-medium">{task.question_id}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Time & People */}
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                            <Users className="h-4 w-4 mr-2" />
                            Time & People
                          </h3>
                          <div className="space-y-3">
                            {task.assignee && (
                              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Assignee</span>
                                <div className="flex items-center">
                                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                                    <User className="h-3 w-3 text-blue-600" />
                                  </div>
                                  <span className="text-sm font-medium">{task.assignee.email}</span>
                                </div>
                              </div>
                            )}
                            {task.due_date && (
                              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Due Date</span>
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm font-medium">
                                    {new Date(task.due_date).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            )}
                            {(task.estimated_hours || task.actual_hours) && (
                              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Time</span>
                                <div className="flex items-center text-sm">
                                  <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                  {task.estimated_hours && `${task.estimated_hours}h est`}
                                  {task.estimated_hours && task.actual_hours && ' / '}
                                  {task.actual_hours && `${task.actual_hours}h actual`}
                                </div>
                              </div>
                            )}
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Created</span>
                              <span className="text-sm font-medium">
                                {new Date(task.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="h-full flex flex-col">
                {/* Comments List */}
                <div className="flex-1 overflow-y-auto">
                  {commentsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                      <span className="text-gray-500">Loading comments...</span>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center px-6">
                      <MessageCircle className="h-12 w-12 text-gray-300 mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No activity yet</h4>
                      <p className="text-gray-500 mb-6">Start the conversation by adding the first comment</p>
                    </div>
                  ) : (
                    <div className="p-6 space-y-4">
                      {comments.map((comment, index) => (
                        <div key={comment.id || index} className="flex space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 truncate">
                                    {comment.user_display_name || comment.user?.email || comment.user_email || 'Unknown User'}
                                  </h4>
                                  <div className="flex items-center text-xs text-gray-500">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {new Date(comment.created_at).toLocaleString()}
                                  </div>
                                </div>
                                {(comment.user_id === user?.id || task.creator_id === user?.id || task.assignee_id === user?.id) && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    disabled={deletingCommentId === comment.id}
                                    className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 rounded hover:bg-gray-100 transition-colors ml-2"
                                    title="Delete comment"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {comment.comment_text}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={commentsEndRef} />
                    </div>
                  )}
                </div>

                {/* Resizable Divider */}
                <div 
                  className="flex-shrink-0 h-1 bg-gray-200 hover:bg-gray-300 cursor-ns-resize transition-colors relative group"
                  onMouseDown={handleCommentsResizeStart}
                  title="Drag to resize comment input area"
                >
                  <div className="absolute inset-x-0 -top-1 -bottom-1 group-hover:bg-blue-200 transition-colors"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex space-x-1">
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                      <div className="w-1 h-1 bg-gray-500 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Expandable Comment Input */}
                <div 
                  className="flex-shrink-0 bg-white border-t border-gray-200"
                  style={{ minHeight: `${Math.max(120, Math.min(commentsPanelHeight, 400))}px` }}
                >
                  <form onSubmit={handleAddComment} className="p-4 h-full flex flex-col">
                    <div className="flex space-x-3 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col">
                        <textarea
                          ref={newCommentRef}
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder-gray-400 text-sm"
                          placeholder="Add a comment..."
                          style={{ minHeight: '60px' }}
                        />
                        <div className="flex justify-between items-center mt-3">
                          <div className="text-xs text-gray-500">
                            💡 Tip: Use @mentions to notify team members
                          </div>
                          <button
                            type="submit"
                            disabled={!newComment.trim() || addingComment}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Send className="h-4 w-4 mr-2" />
                            {addingComment ? 'Adding...' : 'Comment'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'attachments' && (
              <div className="h-full flex flex-col">
                {/* Upload Area */}
                <div className="flex-shrink-0 p-6 border-b border-gray-200">
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive 
                        ? 'border-blue-400 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      multiple
                      onChange={handleFileInputChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    
                    <div className="space-y-3">
                      <div className="flex justify-center">
                        {uploading ? (
                          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        ) : (
                          <Upload className="h-12 w-12 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {uploading ? 'Uploading...' : 'Upload files'}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {uploading 
                            ? 'Please wait while files are being uploaded'
                            : 'Drag and drop files here, or click to browse'
                          }
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          Maximum file size: 10MB
                        </p>
                      </div>
                      {!uploading && (
                        <button 
                          type="button"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Choose Files
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Attachments List */}
                <div className="flex-1 overflow-y-auto">
                  {attachmentsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                      <span className="text-gray-500">Loading attachments...</span>
                    </div>
                  ) : attachments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center px-6">
                      <Paperclip className="h-12 w-12 text-gray-300 mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No attachments yet</h4>
                      <p className="text-gray-500">Upload your first file to get started</p>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="space-y-3">
                        {attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <div className="flex-shrink-0">
                                {getFileIcon(attachment.file_type, attachment.file_name)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                  {attachment.file_name}
                                </h4>
                                <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                                  <span>{formatFileSize(attachment.file_size)}</span>
                                  <span>•</span>
                                  <span>
                                    Uploaded {new Date(attachment.created_at).toLocaleDateString()}
                                  </span>
                                  {attachment.uploaded_by && (
                                    <>
                                      <span>•</span>
                                      <span>by {attachment.uploaded_by.email || 'Unknown'}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAttachment(attachment.id, attachment.file_name)}
                                disabled={deletingAttachmentId === attachment.id}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskDetailPanelPro;